import { and, eq, lt } from "@-/com.npmjs.drizzle-orm";
import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import type { StudySessionCheckpoint } from "#study-session-clock";

/**
 * Write a study session checkpoint: the first one creates the session's row, and every later one
 * moves its end forward.
 *
 * Deliberately an INSERT that changes nothing when the row exists, followed by an UPDATE, and not
 * an `onConflictDoUpdate`: the sync engine logs an upsert as an insert of the whole row, so each
 * checkpoint would upload as a new creation of the session instead of a change to `ended_at`.
 */
export async function saveStudySession(db: ActorDatabase, session: StudySessionCheckpoint) {
  await db(import.meta.location).transaction(async (tx) => {
    const created = await tx
      .insert(actorSchema.studySession)
      .values({ ...session, createdAt: session.startedAt })
      .onConflictDoNothing()
      .returning({ id: actorSchema.studySession.id });
    if (created.length > 0) {
      return;
    }
    await tx
      .update(actorSchema.studySession)
      .set({ endedAt: session.endedAt })
      .where(
        and(
          eq(actorSchema.studySession.id, session.id),
          // Retried/out-of-order checkpoints must never shorten an already persisted session.
          lt(actorSchema.studySession.endedAt, session.endedAt),
        ),
      );
  });
}
